import {
  TGroupDetail,
  TStoryVersionedFrame,
  TSlideDiff,
  TSnapshotDiff,
  TSnapshotField,
  TStoryItem,
  TStorySnapshot,
} from 'types/story';
import { TStoryElementWire } from 'types/storyWire';

import { toWireItems } from './wire';

/**
 * Key-sorted stable stringify.
 *
 * Keys whose value is `undefined` are skipped, which matters more than it looks:
 * `createElement` assigns `undefined` to a dozen optional fields, and those keys disappear
 * on the JSON round-trip to the server. Without this, the same slide serialises one way in
 * memory and another way after a reload, and every comparison reports a change.
 */
const canonical = (value: unknown): string => {
  if (Array.isArray(value)) {
    return `[${value.map(canonical).join(',')}]`;
  }
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    return `{${Object.keys(record)
      .sort()
      .filter((key) => record[key] !== undefined)
      .map((key) => `${JSON.stringify(key)}:${canonical(record[key])}`)
      .join(',')}}`;
  }
  return JSON.stringify(value ?? null);
};

const NON_CONTENT_ITEM_KEYS = ['id', 'v'];

/**
 * Element ids are regenerated by duplicate/paste and may be reassigned server-side, so they
 * describe identity rather than content.
 */
const NON_CONTENT_ELEMENT_KEYS = ['id'];

/** Drag and resize write unrounded percentages; only keyboard nudges are already clamped. */
const round2 = (value: number): number => Math.round(value * 100) / 100;

const omit = (source: Record<string, unknown>, keys: string[]): Record<string, unknown> =>
  Object.fromEntries(Object.entries(source).filter(([key]) => !keys.includes(key)));

const elementContent = (element: TStoryElementWire): Record<string, unknown> => ({
  ...omit(element as unknown as Record<string, unknown>, NON_CONTENT_ELEMENT_KEYS),
  x: round2(element.x),
  y: round2(element.y),
  width: round2(element.width),
  height: round2(element.height),
  rotation: round2(element.rotation ?? 0),
});

/**
 * The single definition of "same slide content" in the codebase.
 *
 * Both sides are pushed through `toWireItems` first, so rules that only ran on save — stripping a
 * text element's background colour, clamping the duration — cannot make a live snapshot look
 * different from an identical draft.
 *
 * EVERY locale bucket is hashed, not just `en`. `v` is shared across buckets, so if this only
 * looked at English a translation-only edit would ship silently under the old revision and app
 * users who had already seen the slide would never be shown the correction.
 */
export const itemContent = (item: TStoryVersionedFrame): string => {
  const buckets = toWireItems([item]);
  return canonical(
    (Object.keys(buckets) as (keyof typeof buckets)[]).sort().map((locale) => {
      const [wire] = buckets[locale] ?? [];
      return {
        locale,
        ...omit(wire as unknown as Record<string, unknown>, NON_CONTENT_ITEM_KEYS),
        elements: (wire?.elements ?? []).map(elementContent),
      };
    })
  );
};

const diffItems = (live: TStoryItem[], draft: TStoryItem[]): TSlideDiff => {
  const liveContent = new Map(live.map((item) => [item.id, itemContent(item)]));
  const draftIds = new Set(draft.map((item) => item.id));

  let added = 0;
  let changed = 0;
  draft.forEach((item) => {
    const before = liveContent.get(item.id);
    if (before === undefined) {
      added += 1;
      return;
    }
    if (before !== itemContent(item)) {
      changed += 1;
    }
  });

  const sharedLive = live.filter((item) => draftIds.has(item.id)).map((item) => item.id);
  const sharedDraft = draft.filter((item) => liveContent.has(item.id)).map((item) => item.id);

  return {
    added,
    removed: live.filter((item) => !draftIds.has(item.id)).length,
    changed,
    reordered: sharedLive.join('|') !== sharedDraft.join('|'),
  };
};

const diffSnapshots = (live: TStorySnapshot, draft: TStorySnapshot): TSnapshotDiff | null => {
  const fields: TSnapshotField[] = [];

  if (canonical(live.title) !== canonical(draft.title)) {
    fields.push('title');
  }
  // Maps now, so compare by value — `!==` on two objects is always true.
  if (canonical(live.coverMediaUrl) !== canonical(draft.coverMediaUrl)) {
    fields.push('cover');
  }
  if (live.startsAt !== draft.startsAt || live.endsAt !== draft.endsAt) {
    fields.push('schedule');
  }
  if (live.segmentId !== draft.segmentId) {
    fields.push('audience');
  }
  if (live.pinned !== draft.pinned) {
    fields.push('pinned');
  }

  const slides = diffItems(live.items, draft.items);
  if (slides.added + slides.removed + slides.changed > 0 || slides.reordered) {
    fields.push('items');
  }

  return fields.length > 0 ? { fields, slides } : null;
};

export const pendingChanges = (detail: TGroupDetail): TSnapshotDiff | null => {
  const live = detail.liveSnapshot;
  const draft = detail.draftSnapshot;

  if (!live) {
    return null;
  }
  if (draft.version <= 0) {
    return null;
  }
  return diffSnapshots(live, draft);
};
